import {
  intakeReviewPartiesHasContent,
  type IntakePartyPerson,
  type IntakeReviewParties,
  type IntakeReviewUser,
} from "@/lib/ops/intakeReviewParties";

function PersonBlock({ person }: { person: IntakePartyPerson }) {
  return (
    <div className="space-y-0.5">
      <div className="font-medium text-gray-900 dark:text-zinc-100">
        {person.name ?? "Unnamed customer"}
      </div>
      {person.phone ? <div>{person.phone}</div> : null}
      {person.secondaryPhone ? <div>{person.secondaryPhone}</div> : null}
      {person.email ? <div className="break-all">{person.email}</div> : null}
    </div>
  );
}

function UserBlock({ user }: { user: IntakeReviewUser }) {
  return (
    <div className="space-y-0.5">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-zinc-400">
        {user.label}
      </div>
      {user.name ? <div className="font-medium text-gray-900 dark:text-zinc-100">{user.name}</div> : null}
      {user.email ? <div className="break-all">{user.email}</div> : null}
      {user.detail ? <div className="text-gray-500 dark:text-zinc-400">{user.detail}</div> : null}
    </div>
  );
}

export function IntakeReviewPartiesPanel({ parties }: { parties: IntakeReviewParties }) {
  if (!intakeReviewPartiesHasContent(parties)) {
    return (
      <div className="rounded-md border border-dashed border-gray-200 px-3 py-3 text-xs text-gray-500 dark:border-zinc-700 dark:text-zinc-400">
        No customer, user, or location fields on this payload.
      </div>
    );
  }

  return (
    <div className="space-y-4 text-xs text-gray-700 dark:text-zinc-300">
      <section className="space-y-1">
        <h3 className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-zinc-400">
          Customer
        </h3>
        {parties.customer ? (
          <PersonBlock person={parties.customer} />
        ) : (
          <p className="text-gray-500 dark:text-zinc-400">Not on this payload.</p>
        )}
      </section>

      <section className="space-y-2">
        <h3 className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-zinc-400">
          Location
        </h3>
        {parties.addresses.map((addr) => (
          <div key={addr.label}>
            <div className="font-medium text-gray-800 dark:text-zinc-200">{addr.label}</div>
            {addr.lines.map((line) => (
              <div key={line}>{line}</div>
            ))}
          </div>
        ))}
        {parties.sellerLocationId ? (
          <div>
            <div className="font-medium text-gray-800 dark:text-zinc-200">Seller location</div>
            <div className="break-all">{parties.sellerLocationId}</div>
          </div>
        ) : null}
        {parties.addresses.length === 0 && !parties.sellerLocationId ? (
          <p className="text-gray-500 dark:text-zinc-400">Not on this payload.</p>
        ) : null}
      </section>

      <section className="space-y-3">
        <h3 className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-zinc-400">
          People
        </h3>
        {parties.users.length > 0 ? (
          parties.users.map((user) => <UserBlock key={`${user.label}:${user.email ?? user.detail ?? ""}`} user={user} />)
        ) : (
          <p className="text-gray-500 dark:text-zinc-400">Not on this payload.</p>
        )}
      </section>
    </div>
  );
}
